import React from "react";
import NextImage from "../NextImage";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/QuickInsights/SearchResults";
import { OpenDataTypeEnum } from "@/src/features/api/openData";

const I18n = I18nUtil.createScoped(Translations);

// 移除固定的 Tags 数组，使用动态传入的 themeData

type SearchOpenData = {
  id?: string;
  name?: string;
  image?: string;
  address?: string;
  type?: OpenDataTypeEnum;
  [key: string]: any;
};

interface SearchResultsProps {
  data: SearchOpenData[];
  isLoading: boolean;
  onItemClick: (item: SearchOpenData) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

// 搜索结果为空时的状态组件 - 根据Figma设计 (node-id=29211-34781) 实现
const EmptySearchState = () => (
  <div className="flex flex-col items-center justify-center h-[440px] w-full py-8">
    {/* 图标容器 */}
    <div className="w-[86px] h-[86px] mb-1 relative">
      {/* 背景圆形 */}
      <NextImage
        src="/assets/images/searchEmpty.webp"
        alt="No results"
        width={86}
        height={86}
      />
    </div>
    {/* 文本 - 使用设计中指定的字体样式和颜色 */}
    <span className="body_l text-content_quaternary">
      {I18n.t("No search results yet")}
    </span>
  </div>
);

// 搜索结果项组件
const SearchResultItem = ({
  item,
  onClick,
}: {
  item: SearchOpenData;
  onClick: () => void;
}) => {
  return (
    <div
      data-openreplay-label="QuickInsights · SearchResults · div"
      className="flex items-center px-3 py-1 hover:bg-background_white_overlay5 rounded-3 cursor-pointer h-[68px] w-full"
      onClick={onClick}
    >
      <div className="w-[35px] h-[35px] mr-2 rounded-full overflow-hidden border-[1px] border-background_white_overlay10">
        <NextImage
          src={item.image}
          alt={item.name || "Token"}
          width={35}
          height={35}
        />
      </div>

      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center w-full overflow-hidden">
          <span
            className="body_medium_m text-content_primary mr-1 truncate max-w-[70%]"
            title={item.name}
          >
            {item.short_name
              ? `${item.short_name} (${item.symbol})`
              : item.name}
          </span>
          {item?.address && (
            <span className="body_m text-content_quaternary">
              /{item?.symbol}
            </span>
          )}
        </div>
        <span className="body_s text-content_tertiary">
          {item.address
            ? `${item.address.substring(0, 6)}...${item.address.substring(item.address.length - 4)}`
            : `${item?.symbol}`}
        </span>
      </div>
    </div>
  );
};

// // 加载状态组件
const LoadingState = () => (
  <div className="flex flex-col space-y-3 py-2">
    {[...Array(10)].map((_, index) => (
      <div key={index} className="flex items-center p-3 animate-pulse">
        <div className="w-8 h-8 mr-3 rounded-full bg-background_white_overlay5"></div>
        <div className="flex-grow">
          <div className="h-4 bg-background_white_overlay5 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-background_white_overlay5 rounded w-1/2"></div>
        </div>
      </div>
    ))}
  </div>
);

export const SearchResults = ({
  data,
  isLoading,
  onItemClick,
}: SearchResultsProps) => {
  if (isLoading) {
    return <LoadingState />;
  }

  if (!data || data.length === 0) {
    return <EmptySearchState />;
  }

  return (
    <div className="flex flex-col overflow-y-auto no-scrollbar w-full">
      {data.map((item, index) => (
        <React.Fragment key={item.id || index}>
          <SearchResultItem
            data-openreplay-label="QuickInsights · SearchResults · SearchResultItem"
            item={item}
            onClick={() => onItemClick(item)}
          />
        </React.Fragment>
      ))}
    </div>
  );
};
